/*
  <capra-film>: the film player. A poster and a play triangle until the visitor
  is near it; then the video file that fits the screen is chosen and loaded.
  Tap plays and pauses, double tap (or double click) toggles fullscreen, and a
  thin progress line, with the sound icon at its end, shows for a moment when the film starts or
  stops and while the pointer moves over it, then both fade together. The icon turns the sound off
  and on while the film plays (a film the visitor starts has sound). A film among a product's photos
  plays by itself, silent, when it is reached and stops when it is left (owner, 2026-10-22); a film
  the visitor starts (the details slideshow's) stops when it is stepped or scrolled away; on a
  touch screen the first tap shows the line and the icon, the next pauses. One film plays at a time. The triangle
  stays in the page, invisible, while the film plays, so the keyboard can pause
  it. A file that will not load puts the poster back. On a phone
  held upright a fullscreen button appears a second into playback.
*/
const films = new Set();
const portrait = matchMedia('(max-width: 798px) and (orientation: portrait)');

class CapraFilm extends HTMLElement {
  connectedCallback() {
    films.add(this);
    this.video = this.querySelector('video');
    this.link = this.querySelector('.film__play');
    this.bar = this.querySelector('.film__bar');
    this.range = this.bar.querySelector('input');
    this.expand = this.querySelector('.film__fullscreen');
    this.sound = this.querySelector('.film__sound');
    this.started = false;
    this.lastTap = 0;
    this.autoplays = Boolean(this.closest('capra-gallery'));

    // Near the screen: choose the file and let the browser read the start of it.
    new IntersectionObserver((entries, observer) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.prepare();
        observer.disconnect();
      }
    }, { rootMargin: '400px 0px' }).observe(this);
    this.link.addEventListener('pointerenter', () => this.prepare(), { once: true });
    if (this.autoplays) this.watch();
    else this.leave();

    this.labels = { play: this.link.getAttribute('aria-label'), pause: this.dataset.pause };
    this.link.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation(); // not also a tap on the film, which would toggle it twice
      this.started ? this.toggle() : this.play();
    });
    this.addEventListener('click', (event) => {
      if (this.closest('capra-gallery')?.dragged) return; // the end of a drag through the photos, not a tap
      if (!this.started || event.target.closest('.film__bar, .film__fullscreen, .film__sound')) return;
      // A finger: the first tap brings the line and the sound icon, the next pauses
      if (event.pointerType === 'touch' && !this.bar.classList.contains('is-shown')) return this.reveal();
      this.toggle();
    });
    this.addEventListener('dblclick', (event) => {
      if (this.started && !event.target.closest('.film__bar, .film__fullscreen, .film__sound')) this.fullscreen();
    });
    this.addEventListener('touchend', (event) => {
      if (!this.started || event.target.closest('.film__bar, .film__fullscreen, .film__sound')) return;
      const now = Date.now();
      if (now - this.lastTap < 320) {
        event.preventDefault();
        if (this.video.paused) this.play(); // the first tap paused it; fullscreen keeps it playing
        this.fullscreen();
        this.lastTap = 0;
      } else {
        this.lastTap = now;
      }
    });
    this.sound?.addEventListener('click', (event) => {
      event.stopPropagation();
      this.video.muted = !this.video.muted;
      this.sound.setAttribute('aria-pressed', String(!this.video.muted));
    });
    // A pointer moving over the film shows where it is, then the line fades again
    this.addEventListener('pointermove', (event) => {
      if (event.pointerType === 'mouse') this.reveal();
    });
    this.expand.addEventListener('click', (event) => {
      event.stopPropagation();
      this.fullscreen();
    });
    this.video.addEventListener('timeupdate', () => {
      if (!this.seeking && this.video.duration) this.range.value = (this.video.currentTime / this.video.duration) * 100;
    });
    this.range.addEventListener('input', () => {
      this.seeking = true;
      if (this.video.duration) this.video.currentTime = (this.range.value / 100) * this.video.duration;
    });
    this.range.addEventListener('change', () => (this.seeking = false));
    this.video.addEventListener('volumechange', () => this.syncSound());
    this.video.addEventListener('playing', () => this.classList.add('is-started'));
    this.video.addEventListener('play', () => this.state(true));
    this.video.addEventListener('pause', () => this.state(false));
    this.onScreen = () => this.sync();
    document.addEventListener('fullscreenchange', this.onScreen);
    portrait.addEventListener('change', this.onScreen);
  }

  disconnectedCallback() {
    films.delete(this);
    clearTimeout(this.timer);
    clearTimeout(this.peek);
    document.removeEventListener('fullscreenchange', this.onScreen);
    portrait.removeEventListener('change', this.onScreen);
  }

  /** The smallest file that is at least as sharp as the screen, capped at 1080p; the smallest on Data Saver. */
  prepare() {
    if (this.video.getAttribute('src')) return;
    const files = this.video.dataset.sources.split('|').map((item) => {
      const [url, height] = item.split('@');
      return { url, height: Number(height) };
    });
    const saver = navigator.connection?.saveData;
    const want = Math.min(1080, Math.round((this.clientWidth * (devicePixelRatio || 1)) / (16 / 9)));
    const pick = saver ? files[0] : files.find((file) => file.height >= want) || files[files.length - 1];
    this.video.src = pick.url;
    this.video.preload = saver ? 'none' : 'metadata';
  }

  async play() {
    this.prepare();
    for (const other of films) if (other !== this) other.video.pause();
    this.started = true;
    this.video.hidden = false; // the poster stays until the first frame plays ('playing')
    try {
      await this.video.play();
    } catch (error) {
      if (error.name === 'AbortError') return; // paused or seeked while starting: not a failure
      // The file would not load or the browser refused: back to the poster, and a tap tries again.
      this.started = false;
      this.classList.remove('is-started');
      this.video.hidden = true;
      this.state(false);
    }
  }

  /** In a product's photos: plays, silent, once most of it is on screen; pauses when it leaves. */
  watch() {
    new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        if (this.stopped) return;
        this.video.muted = true; // browsers only start a film by itself without sound
        this.play();
      } else {
        this.stopped = false;
        if (this.started) this.video.pause();
      }
    }, { threshold: 0.6 }).observe(this);
  }

  /** Started by the visitor: pauses once it is off screen (another slide, or scrolled past). */
  leave() {
    new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting && this.started) this.video.pause();
    }).observe(this);
  }

  toggle() {
    if (this.video.paused) this.play();
    else {
      this.stopped = this.autoplays; // paused by the visitor: it stays paused until the photo is left and reached again
      this.video.pause();
    }
  }

  syncSound() {
    this.sound?.setAttribute('aria-pressed', String(!this.video.muted));
  }

  state(playing) {
    this.classList.toggle('is-playing', playing);
    this.link.setAttribute('aria-label', playing ? this.labels.pause : this.labels.play);
    this.reveal();
    if (this.sound) this.sound.hidden = !playing;
    this.syncSound();
    this.sync();
  }

  /** The progress line, for a moment: it stays while the pointer is on it or it has focus. */
  reveal() {
    if (!this.started) return;
    this.bar.hidden = false;
    this.bar.classList.add('is-shown');
    clearTimeout(this.peek);
    this.peek = setTimeout(() => {
      if (this.bar.matches(':hover, :focus-within')) this.reveal();
      else this.bar.classList.remove('is-shown');
    }, 2200);
  }

  async fullscreen() {
    clearTimeout(this.timer);
    if (document.fullscreenElement) return document.exitFullscreen();
    if (this.requestFullscreen) await this.requestFullscreen().catch(() => {});
    else this.video.webkitEnterFullscreen?.(); // iPhone
  }

  /** The upright-phone fullscreen button appears a second into playback. */
  sync() {
    clearTimeout(this.timer);
    this.expand.hidden = true;
    if (this.started && !this.video.paused && !document.fullscreenElement && portrait.matches) {
      this.timer = setTimeout(() => (this.expand.hidden = this.video.paused), 1000);
    }
  }
}

if (!customElements.get('capra-film')) customElements.define('capra-film', CapraFilm);
